<template>
    <div class="financeApplication">
        <template v-if="!isComplete">
            <FinanceApplicationProgress :current-step="step" :total-steps="totalSteps" />
            <FinanceApplicationTitle :title="currentStep.title" :excerpt="currentStep.excerpt" />

            <div class="financeApplication__body">
                <Component :is="currentStep.component"
                           :initial-values="currentStep.initialValues"
                           :window-width="windowWidth"
                           v-on="currentStep.events"
                />
            </div>

            <div class="financeApplication__footer">
                <button type="button"
                        class="financeApplication__button financeApplication__button--outline"
                        :disabled="isBackDisabled"
                        @click="onDecreaseStep"
                >
                    Back
                </button>

                <button type="button" class="financeApplication__button" @click="onIncreaseStep" :disabled="isNextDisabled || isSubmitting">
                    {{ currentStep.step === totalSteps ? 'Finish' : 'Next' }}
                </button>
            </div>
        </template>
        <FinanceApplicationComplete
            :utm-campaign="utmCampaign"
            :utm-medium="utmMedium"
            :utm-source="utmSource"
            @close="$emit('close')"
            v-else />
    </div>
</template>
<script>
import FinanceApplicationProgress from './FinanceApplicationProgress.vue'
import FinanceApplicationTitle from './FinanceApplicationTitle.vue'
import FinanceApplicationDrivingLicence from './FinanceApplicationDrivingLicence.vue'
import FinanceApplicationMarital from './FinanceApplicationMarital.vue'
import FinanceApplicationPassport from './FinanceApplicationPassport.vue'
import FinanceApplicationBirthday from './FinanceApplicationBirthday.vue'
import FinanceApplicationEmploymentStatus from './FinanceApplicationEmploymentStatus.vue'
import FinanceApplicationEmploymentEmployer from './FinanceApplicationEmploymentEmployer.vue'
import FinanceApplicationEmploymentPostcode from './FinanceApplicationEmploymentPostcode.vue'
import FinanceApplicationEmploymentDuration from './FinanceApplicationEmploymentDuration.vue'
import FinanceApplicationEmploymentIncome from './FinanceApplicationEmploymentIncome.vue'
import FinanceApplicationAddressPostcode from './FinanceApplicationAddressPostcode.vue'
import FinanceApplicationAddressStatus from './FinanceApplicationAddressStatus.vue'
import FinanceApplicationAddressDuration from './FinanceApplicationAddressDuration.vue'
import FinanceApplicationBankDetails from './FinanceApplicationBankDetails.vue'
import FinanceApplicationBankDuration from './FinanceApplicationBankDuration.vue'
import FinanceApplicationPersonal from './FinanceApplicationPersonal.vue'
import FinanceApplicationContact from './FinanceApplicationContact.vue'
import FinanceApplicationComplete from './FinanceApplicationComplete.vue'
import { gtagTrack } from '../../utils/gtag-track'

export default {
    components: {
        FinanceApplicationTitle,
        FinanceApplicationProgress,
        FinanceApplicationDrivingLicence,
        FinanceApplicationMarital,
        FinanceApplicationPassport,
        FinanceApplicationBirthday,
        FinanceApplicationEmploymentStatus,
        FinanceApplicationEmploymentEmployer,
        FinanceApplicationEmploymentPostcode,
        FinanceApplicationEmploymentDuration,
        FinanceApplicationEmploymentIncome,
        FinanceApplicationAddressPostcode,
        FinanceApplicationAddressStatus,
        FinanceApplicationAddressDuration,
        FinanceApplicationBankDetails,
        FinanceApplicationBankDuration,
        FinanceApplicationPersonal,
        FinanceApplicationContact,
        FinanceApplicationComplete
    },
    props: {
        totalCostOfCredit: {
            type: Number,
            default: 0
        },
        totalFunded: {
          type: Number,
          default: 0
        },
        term: {
            type: Number,
            default: 0
        },
        deposit: {
            type: Number,
            default: 0
        },
        totalMonthly: {
            type: Number,
            default: 0
        },
        financeType: {
            type: String
        },
        utmCampaign: {
            type: String
        },
        utmMedium: {
            type: String
        },
        utmSource: {
            type: String
        }
    },
    data() {
        return {
            step: 1,
            params: {
                drivingLicence: '',
                maritalStatus: '',
                passport: '',
                birthday: '',
                bankSortCode: '',
                bankAccountNumber: '',
                timeWithBankYears: '',
                timeWithBankMonths: '',
                title: '',
                firstName: '',
                lastName: '',
                mobileNumber: '',
                emailAddress: '',

                employments: [
                    {
                        status: '',
                        employer: '',
                        jobTitle: '',
                        years: '',
                        months: '',
                        salary: '',
                        addressLine1: '',
                        addressLine2: '',
                        city: '',
                        postcode: '',
                        confirmed: false
                    }
                ],
                addresses: [
                    {
                        addressLine1: '',
                        addressLine2: '',
                        city: '',
                        postcode: '',
                        isManualAddressFormVisible: false,
                        status: '',
                        livedYears: '',
                        livedMonths: '',
                        confirmed: false
                    },
                ],
                termsAgreement: false,
                marketingAgreement: false,
            },
            validSteps: {
                drivingLicence: false,
                maritalStatus: false,
                passport: false,
                birthday: false,
                employmentStatus0: false,
                employmentEmployer0: false,
                employmentPostcode0: false,
                employmentIncome0: false,
                employmentDuration0: false,
                addressPostcode0: false,
                addressStatus0: false,
                addressDuration0: false,
                bankDetails: false,
                bankDuration: false,
                personal: false,
                contact: false
            },
            isComplete: false,
            isSubmitting: false,
            windowWidth: 0
        }
    },
    created() {
        this.windowWidth = window.innerWidth;
        window.addEventListener('resize', this.setWindowWidth);
    },
    destroyed() {
        window.removeEventListener('resize', this.setWindowWidth)
    },
    mounted() {
        this.sendVirtualPage()
    },
    methods: {
        setWindowWidth() {
            this.windowWidth = window.innerWidth;
        },
        onDecreaseStep() {
            if (this.isBackDisabled) {
                return
            }

            this.step--
        },
        onIncreaseStep() {
            if (this.isNextDisabled) {
                return
            }

            if (this.currentStep.step === this.totalSteps) {
                this.onSubmit()
                return
            }

            this.step++
        },
        onComponentChange({ key, value }) {
            this.params[key] = value
        },
        onComponentValidate({ key, valid }) {
            this.validSteps[key] = valid
        },
        onComponentAddressChange({ key, value }) {
            this.$set(this.params.addresses, key, value)
        },
        onComponentAddressDurationValidate({ index, valid }) {
            this.$set(this.params.addresses[index], 'confirmed', valid)

            if (this.addressesYearsTotal < 3) {
                if (this.isAddressesConfirmed) {
                    this.$set(this.validSteps, `addressPostcode${index + 1}`, false)
                    this.$set(this.validSteps, `addressStatus${index + 1}`, false)
                    this.$set(this.validSteps, `addressDuration${index + 1}`, false)

                    this.params.addresses.push({
                        addressLine1: '',
                        addressLine2: '',
                        city: '',
                        postcode: '',
                        isManualAddressFormVisible: false,
                        status: '',
                        livedYears: '',
                        livedMonths: '',
                        confirmed: false,
                    })
                }
            } else {
                // remove any additional created addresses which aren't filled out
                const indexesToRemove = this.params.addresses
                    .map((address, i) => ({
                    ...address,
                    index: i
                }))
                    .filter((a, i) => i !== 0 && a.postcode === '' && a.status === '' && a.livedYears === '' && a.livedMonths === '')
                    .map((a) => a.index)

                indexesToRemove.forEach((indexToRemove) => {
                    this.$delete(this.validSteps, `addressPostcode${indexToRemove}`)
                    this.$delete(this.validSteps, `addressStatus${indexToRemove}`)
                    this.$delete(this.validSteps, `addressDuration${indexToRemove}`)
                })

                const filteredAddresses = this.params.addresses.filter((_, i) => !indexesToRemove.includes(i))
                this.params.addresses = filteredAddresses
            }
        },
        onComponentEmploymentChange({ key, value }) {
            this.$set(this.params.employments, key, value)
        },
        onComponentEmploymentDurationValidate({ index, valid }) {
            this.$set(this.params.employments[index], 'confirmed', valid)

            if (this.employmentYearsTotal < 3) {
                if (this.isEmploymentsConfirmed) {
                    this.$set(this.validSteps, `employmentStatus${index + 1}`, false)
                    this.$set(this.validSteps, `employmentEmployer${index + 1}`, false)
                    this.$set(this.validSteps, `employmentPostcode${index + 1}`, false)
                    this.$set(this.validSteps, `employmentIncome${index + 1}`, false)
                    this.$set(this.validSteps, `employmentDuration${index + 1}`, false)

                    this.params.employments.push({
                        status: '',
                        employer: '',
                        jobTitle: '',
                        addressLine1: '',
                        addressLine2: '',
                        city: '',
                        postcode: '',
                        years: '',
                        months: '',
                        salary: '',
                        confirmed: false
                    })
                }
            } else {
                const indexesToRemove = this.params.employments
                    .map((employment, i) => ({
                        ...employment,
                        index: i
                    }))
                    .filter((e, i) => i !== 0 && e.status === '' && e.employer === '' && e.jobTitle === '' && e.postcode === '' && e.years === '' && e.months === '' && e.salary === '')
                    .map((e) => e.index)

                indexesToRemove.forEach((indexToRemove) => {
                    this.$delete(this.validSteps, `employmentStatus${indexToRemove}`)
                    this.$delete(this.validSteps, `employmentEmployer${indexToRemove}`)
                    this.$delete(this.validSteps, `employmentPostcode${indexToRemove}`)
                    this.$delete(this.validSteps, `employmentIncome${indexToRemove}`)
                    this.$delete(this.validSteps, `employmentDuration${indexToRemove}`)
                })

                const filteredEmployments = this.params.employments.filter((_, i) => !indexesToRemove.includes(i))
                this.params.employments = filteredEmployments
            }
        },
        async onSubmit() {
            if (this.isSubmitting) {
                return
            }

            this.isSubmitting = true

            const addresses = this.params.addresses.map((a) => ({
                address_line_1: a.addressLine1,
                address_line_2: a.addressLine2,
                city: a.city,
                postcode: a.postcode,
                status: a.status,
                years: a.livedYears,
                months: a.livedMonths,
            }))

            const employments = this.params.employments.map((e) => ({
                status: e.status,
                employer: e.employer,
                job_title: e.jobTitle,
                address_line_1: e.addressLine1,
                address_line_2: e.addressLine2,
                city: e.city,
                postcode: e.postcode,
                years: e.years,
                months: e.months,
                salary: e.salary,
            }))

            const params = {
                driving_licence: this.params.drivingLicence,
                marital_status: this.params.maritalStatus,
                passport: this.params.passport,
                birthday: this.params.birthday,
                bank_sort_code: this.params.bankSortCode,
                bank_account_number: this.params.bankAccountNumber,
                time_with_bank_years: this.params.timeWithBankYears,
                time_with_bank_months: this.params.timeWithBankMonths,
                title: this.params.title,
                first_name: this.params.firstName,
                last_name: this.params.lastName,
                mobile_number: this.params.mobileNumber,
                email_address: this.params.emailAddress,
                terms_agreement: this.params.termsAgreement,
                marketing_agreement: this.params.marketingAgreement,
                amount_to_borrow: this.totalFunded,
                deposit: this.deposit,
                term: this.term,
                total_monthly: this.totalMonthly,
                finance_type: this.financeType,
                utm_campaign: this.utmCampaign,
                utm_source: this.utmSource,
                utm_medium: this.utmMedium,
                addresses,
                employments
            }

            try {
                const res = await fetch('/plugins/AutoConvert/ajax/submit.php', {
                    method: 'POST',
                    headers: new Headers({
                        'Accept': 'application/json'
                    }),
                    credentials: 'same-origin',
                    body: JSON.stringify(params),
                })

                const body = await res.json()

                if (!res.ok) {
                    return await Promise.reject(body);
                }

                this.isComplete = true
            } catch (e) {
                console.error(e)
            } finally {
                this.isSubmitting = false
            }
        },
        slugify(string) {
            const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìıİłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;'
            const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------'
            const p = new RegExp(a.split('').join('|'), 'g')

            return string.toString().toLowerCase()
                .replace(/\s+/g, '-')
                .replace(p, c => b.charAt(a.indexOf(c)))
                .replace(/&/g, '-and-')
                .replace(/[^\w\-]+/g, '')
                .replace(/\-\-+/g, '-')
                .replace(/^-+/, '')
                .replace(/-+$/, '')
        },
        sendVirtualPage() {
            const slug = this.currentStep.virtualPageSlug

            if (!slug) {
                return
            }

            const url = window.location.origin + '/finance/' + this.slugify(slug)
            const params = `?utm_source=${this.utmSource}&utm_campaign=${this.utmCampaign}&utm_medium=${this.utmMedium}`
            const pageTitle = this.currentStep.title + ' - Step ' + this.currentStep.step
            const dataLayerObj = {
                event: 'virtualPageView',
                virtualPageURL: url,
                virtualPageTitle: pageTitle,
            }

            dataLayer.push(dataLayerObj)

            gtagTrack('config', 'G-1VFETT6XL1', {
                page_title: pageTitle,
                page_location: url + params
            })

            const historyUrl = url + params

            history.pushState({}, '', historyUrl)
        },
        getFormData(formData, data, previousKey) {
            if (data instanceof Object) {
                Object.keys(data).forEach(key => {
                    const value = data[key];
                    if (value instanceof Object && !Array.isArray(value)) {
                        return this.getFormData(formData, value, key);
                    }
                    if (previousKey) {
                        key = `${previousKey}[${key}]`;
                    }
                    if (Array.isArray(value)) {
                        value.forEach(val => {
                            formData.append(`${key}[]`, val);
                        });
                    } else {
                        formData.append(key, value);
                    }
                });
            }

            return formData
        }
    },
    computed: {
        steps() {
            const steps = [
                {
                    step: null,
                    validStepKey: 'drivingLicence',
                    component: 'FinanceApplicationDrivingLicence',
                    ref: 'drivingLicence',
                    title: 'Driving Licence',
                    virtualPageSlug: 'driving-licence',
                    excerpt: 'What kind do you have?',
                    events: {
                        change: this.onComponentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        drivingLicence: this.params.drivingLicence
                    }
                },
                {
                    step: null,
                    validStepKey: 'maritalStatus',
                    component: 'FinanceApplicationMarital',
                    ref: 'marital',
                    title: 'Marital Status',
                    virtualPageSlug: 'marital-status',
                    excerpt: 'Are you?',
                    events: {
                        change: this.onComponentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        maritalStatus: this.params.maritalStatus
                    }
                },
                {
                    step: null,
                    validStepKey: 'passport',
                    component: 'FinanceApplicationPassport',
                    ref: 'passport',
                    title: 'UK Passport',
                    virtualPageSlug: 'uk-passport',
                    excerpt: 'Do you hold a valid UK passport?',
                    events: {
                        change: this.onComponentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        passport: this.params.passport
                    }
                },
                {
                    step: null,
                    validStepKey: 'birthday',
                    component: 'FinanceApplicationBirthday',
                    ref: 'birthday',
                    title: 'Date of Birth',
                    virtualPageSlug: 'birthday',
                    excerpt: 'What is your date of birth?',
                    events: {
                        change: this.onComponentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        birthday: this.params.birthday
                    }
                },
                {
                    step: null,
                    validStepKey: 'bankDetails',
                    component: 'FinanceApplicationBankDetails',
                    ref: 'bankDetails',
                    title: 'Your Financial Details',
                    virtualPageSlug: 'bank-details',
                    excerpt: 'No payment will be taken. Supplying your bank details allows us to use a wider panel of lenders for your proposal and helps with the identification checks of your application.',
                    events: {
                        change: this.onComponentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        bankSortCode: this.params.bankSortCode,
                        bankAccountNumber: this.params.bankAccountNumber
                    }
                },
                {
                    step: null,
                    validStepKey: 'bankDuration',
                    component: 'FinanceApplicationBankDuration',
                    ref: 'bankDuration',
                    title: 'Estimated time with bank',
                    virtualPageSlug: 'bank-duration',
                    excerpt: 'All information is encrypted. Bank details are required by most lenders in order to make a decision on your application.',
                    events: {
                        change: this.onComponentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        timeWithBankYears: this.params.timeWithBankYears,
                        timeWithBankMonths: this.params.timeWithBankMonths,
                    }
                },
                {
                    step: null,
                    validStepKey: 'personal',
                    component: 'FinanceApplicationPersonal',
                    ref: 'personal',
                    title: 'Almost done',
                    virtualPageSlug: 'personal-details',
                    excerpt: 'Let us know who you are',
                    events: {
                        change: this.onComponentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        title: this.params.title,
                        firstName: this.params.firstName,
                        lastName: this.params.lastName
                    }
                },
                {
                    step: null,
                    validStepKey: 'contact',
                    component: 'FinanceApplicationContact',
                    ref: 'contact',
                    title: 'Submit Application',
                    virtualPageSlug: 'contact-and-submit',
                    excerpt: 'How can we contact you?',
                    events: {
                        change: this.onComponentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        mobileNumber: this.params.mobileNumber,
                        emailAddress: this.params.emailAddress,
                        termsAgreement: this.params.termsAgreement,
                        marketingAgreement: this.params.marketingAgreement,
                    }
                },
            ]

            // Add in employment steps
            const passportIndex = steps.findIndex((s) => s.ref === 'passport')
            const employmentStepsToAdd = []

            this.params.employments.forEach((e, i) => {
                let stepTitle = 'Employment'
                let virtualPageSlugPrefix = 'employment-'

                if (i !== 0) {
                    stepTitle = 'Previous Employment'
                    // virtualPageSlugPrefix = 'previous-employment-'
                }

                employmentStepsToAdd.push({
                    step: null,
                    validStepKey: 'employmentStatus' + i,
                    component: 'FinanceApplicationEmploymentStatus',
                    ref: 'employmentStatus',
                    title: stepTitle,
                    virtualPageSlug: i === 0 ? virtualPageSlugPrefix + 'status' : null,
                    excerpt: i === 0 ? 'What is your employment status?' : 'As you have been at your employment for less than 3 years, please enter your previous employment details',
                    events: {
                        'change-employment': this.onComponentEmploymentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        index: i,
                        employment: this.params.employments[i]
                    }
                })

                employmentStepsToAdd.push({
                    step: null,
                    validStepKey: 'employmentEmployer' + i,
                    component: 'FinanceApplicationEmploymentEmployer',
                    ref: 'employmentEmployer',
                    title: stepTitle,
                    virtualPageSlug: i === 0 ? virtualPageSlugPrefix + 'employer' : null,
                    excerpt: 'Who is your employer? We never contact your employer',
                    events: {
                        'change-employment': this.onComponentEmploymentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        index: i,
                        employment: this.params.employments[i]
                    }
                })

                employmentStepsToAdd.push({
                    step: null,
                    validStepKey: 'employmentPostcode' + i,
                    component: 'FinanceApplicationEmploymentPostcode',
                    ref: 'employmentPostcode',
                    title: stepTitle,
                    virtualPageSlug: i === 0 ? virtualPageSlugPrefix + 'postcode' : null,
                    excerpt: 'What is your employer\'s postcode?',
                    events: {
                        'change-employment': this.onComponentEmploymentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        index: i,
                        employment: this.params.employments[i]
                    }
                })

                employmentStepsToAdd.push({
                    step: null,
                    validStepKey: 'employmentIncome' + i,
                    component: 'FinanceApplicationEmploymentIncome',
                    ref: 'employmentIncome',
                    title: stepTitle + ' Monthly Income',
                    virtualPageSlug: i === 0 ? virtualPageSlugPrefix + 'income' : null,
                    excerpt: 'What is your monthly take home income, after tax?',
                    events: {
                        'change-employment': this.onComponentEmploymentChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        index: i,
                        employment: this.params.employments[i]
                    }
                })

                employmentStepsToAdd.push({
                    step: null,
                    validStepKey: 'employmentDuration' + i,
                    component: 'FinanceApplicationEmploymentDuration',
                    ref: 'employmentDuration',
                    title: stepTitle,
                    virtualPageSlug: i === 0 ? virtualPageSlugPrefix + 'duration' : null,
                    excerpt: 'How long have you worked there?',
                    events: {
                        'change-employment': this.onComponentEmploymentChange,
                        'validate-employment-duration': this.onComponentEmploymentDurationValidate,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        index: i,
                        employment: this.params.employments[i]
                    }
                })
            })

            // console.log({ employmentStepsToAdd })

            steps.splice(passportIndex, 0, ...employmentStepsToAdd)

            const birthdayIndex = steps.findIndex((s) => s.ref === 'birthday')
            const addressStepsToAdd = []

            this.params.addresses.forEach((a, i) => {
                let stepTitle = 'Address Details'
                let virtualPageSlugPrefix = 'address-'

                if (i !== 0) {
                    stepTitle = 'Previous Address Details'
                    // virtualPageSlugPrefix = 'previous-address-'
                }

                // Add postcode step
                addressStepsToAdd.push({
                    step: null,
                    validStepKey: 'addressPostcode' + i,
                    component: 'FinanceApplicationAddressPostcode',
                    ref: 'postcode' + i,
                    title: stepTitle,
                    virtualPageSlug: i === 0 ? virtualPageSlugPrefix + 'postcode' : null,
                    excerpt: i === 0 ? 'What is your postcode?' : 'As you have lived at your current address for less than 3 years, please enter your previous address',
                    events: {
                        'change-address': this.onComponentAddressChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        index: i,
                        address: this.params.addresses[i]
                    }
                })

                // Add status step
                addressStepsToAdd.push({
                    step: null,
                    validStepKey: 'addressStatus' + i,
                    component: 'FinanceApplicationAddressStatus',
                    ref: 'addressStatus' + i,
                    title: stepTitle,
                    virtualPageSlug: i === 0 ? virtualPageSlugPrefix + 'status' : null,
                    excerpt: 'What is your residential status?',
                    events: {
                        'change-address': this.onComponentAddressChange,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        homeStatus: this.params.homeStatus,
                        index: i,
                        address: this.params.addresses[i]
                    }
                })

                // Add duration step
                addressStepsToAdd.push({
                    step: null,
                    validStepKey: 'addressDuration' + i,
                    component: 'FinanceApplicationAddressDuration',
                    ref: 'addressDuration' + i,
                    title: stepTitle,
                    virtualPageSlug: i === 0 ? virtualPageSlugPrefix + 'duration' : null,
                    excerpt: null,
                    events: {
                        'change-address': this.onComponentAddressChange,
                        'validate-address-duration': this.onComponentAddressDurationValidate,
                        validate: this.onComponentValidate
                    },
                    initialValues: {
                        index: i,
                        address: this.params.addresses[i]
                    }
                })
            })

            steps.splice(birthdayIndex, 0, ...addressStepsToAdd)

            // steps.splice(lastEmploymentDurationIndex, 0, ...addressStepsToAdd)

            // console.log({ steps })

            return steps.map((s, i) => ({
                ...s,
                step: i + 1
            }))
        },
        totalSteps() {
            return this.steps.length
        },
        currentStep() {
            return this.steps.find((s) => s.step === this.step)
        },
        isBackDisabled() {
            return this.step <= 1
        },
        isCurrentStepValid() {
            return this.validSteps[this.currentStep.validStepKey]
        },
        isNextDisabled() {
            return !this.isCurrentStepValid || this.currentStep.step > this.totalSteps
        },
        isAddressesConfirmed() {
            return this.params.addresses.every((a) => a.confirmed === true)
        },
        isEmploymentsConfirmed() {
            return this.params.employments.every((e) => e.confirmed === true)
        },
        addressesYearsTotal() {
            const livedYears = this.params.addresses.map((x) => parseInt(x.livedYears) || 0)
            const livedMonths = this.params.addresses.map((x) => parseInt(x.livedMonths) || 0)

            const years = livedYears.reduce((acc, curr) => acc + curr, 0)
            const months = livedMonths.reduce((acc, curr) => acc + curr, 0)

            return years + (months / 12)
        },
        employmentYearsTotal() {
            const timeYears = this.params.employments.map((x) => parseInt(x.years) || 0)
            const timeMonths = this.params.employments.map((x) => parseInt(x.months) || 0)

            const years = timeYears.reduce((acc, curr) => acc + curr, 0)
            const months = timeMonths.reduce((acc, curr) => acc + curr, 0)

            return years + (months / 12)
        }
    },
    watch: {
        step(val, oldVal) {
            if (val === oldVal) {
                return
            }

            this.sendVirtualPage()
        }
    }
}
</script>